import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Fuel, TrendingUp, HardHat } from "lucide-react";

const DIESELS_LOGO = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/v3ubrgjb_Vector.png";
const INNOVEST_LOGO = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/m17x1mzm_Vector-1.png";

const COMPANIES = [
  {
    icon: Fuel,
    logo: DIESELS_LOGO,
    name: "Sharda Diesels",
    tagline: "Power Systems Manufacturing",
    blurb: "Heavy-duty diesel power systems for construction, mining, telecom, and standby applications.",
  },
  {
    icon: TrendingUp,
    logo: INNOVEST_LOGO,
    name: "Sharda Innovest",
    tagline: "Strategic Investments & Ventures",
    blurb: "Strategic capital deployed across clean energy, advanced manufacturing, and technology.",
  },
  {
    icon: HardHat,
    name: "Sharda Infra",
    tagline: "Infrastructure & Industrial Development",
    blurb: "Industrial parks, facility construction, land development, and turnkey project execution.",
  },
];

export default function ShardaGroupPreviewSection() {
  return (
    <section
      data-testid="sharda-group-preview-section"
      className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden"
    >
      <div className="section-glow absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-4"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // The Sharda Group
          </span>
        </motion.div>

        <div className="mb-10 md:mb-14">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={fadeInUp}
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight mb-4 md:mb-5">
              <span className="block">Backed by a legacy of</span>
              <span className="block text-[#1E96FC]">industrial excellence</span>
            </h2>
            <p className="text-slate-400 leading-relaxed text-sm max-w-2xl">
              Sharda Energy Tech is part of the Sharda Group — a diversified
              industrial conglomerate operating across power, investments, and
              infrastructure for decades.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
        >
          {COMPANIES.map((c, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              data-testid={`group-preview-card-${i}`}
              className="glass-card rounded-sm p-6 md:p-7 group hover:bg-[#003771]/20 transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-sm flex items-center justify-center transition-colors overflow-hidden flex-shrink-0 ${c.logo ? "bg-white group-hover:bg-white/90" : "bg-[#1E96FC]/10 group-hover:bg-[#1E96FC]/20"}`}
                >
                  {c.logo ? (
                    <img src={c.logo} alt={c.name} className="w-7 h-7 object-contain" />
                  ) : (
                    <c.icon className="w-5 h-5 text-[#1E96FC]" />
                  )}
                </div>
                <h3 className="text-base md:text-lg font-bold uppercase text-slate-100 tracking-wide leading-tight">
                  {c.name}
                </h3>
              </div>
              <span className="text-[10px] text-[#1E96FC] uppercase tracking-[0.2em] mb-3">
                {c.tagline}
              </span>
              <p className="text-sm text-slate-400 leading-relaxed">
                {c.blurb}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mt-8 md:mt-10 flex justify-start"
        >
          <Link to="/sharda-group" data-testid="explore-sharda-group-btn">
            <Button
              className="bg-[#1E96FC] text-white hover:bg-[#1E96FC]/90 rounded-sm px-6 md:px-8 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.15em] font-bold transition-all duration-200 hover:scale-[1.02] min-h-[48px]"
            >
              Explore Sharda Group
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
